import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {((event: Event) => void) | undefined} */
  #backupClickHandler;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    // Add a backup click listener directly on the button for mobile/iOS compatibility
    // This ensures the form submission happens even if event delegation fails
    setTimeout(() => {
      if (this.refs.addToCartButton) {
        console.log('[AddToCart] Setting up backup click listener on button');
        this.#setupBackupClickListener();
      }
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);

    // Remove backup click listener
    if (this.#backupClickHandler && this.refs.addToCartButton) {
      this.refs.addToCartButton.removeEventListener('click', this.#backupClickHandler);
    }
  }

  /**
   * Sets up a backup click listener for mobile/iOS compatibility
   */
  #setupBackupClickListener() {
    if (!this.refs.addToCartButton || this.#backupClickHandler) return;

    this.#backupClickHandler = (event) => {
      console.log('[AddToCart] Backup click listener triggered');
      const form = this.closest('form');
      if (form && this.refs.addToCartButton.type === 'submit') {
        console.log('[AddToCart] Backup: Triggering form submission');
        setTimeout(() => {
          try {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();
            } else {
              // Fallback: create and dispatch submit event
              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
              const canSubmit = form.dispatchEvent(submitEvent);
              if (canSubmit) {
                form.submit();
              }
            }
          } catch (error) {
            console.error('[AddToCart] Backup: Error submitting form:', error);
          }
        }, 50);
      }
    };

    this.refs.addToCartButton.addEventListener('click', this.#backupClickHandler, { capture: false });
    console.log('[AddToCart] Backup click listener attached');
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    console.log('[AddToCart] Disabling button');
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = true;
      console.log('[AddToCart] Button disabled state:', this.refs.addToCartButton.disabled);
    } else {
      console.error('[AddToCart] No button found to disable');
    }
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    console.log('[AddToCart] Enabling button');
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = false;
      console.log('[AddToCart] Button disabled state:', this.refs.addToCartButton.disabled);
      console.log('[AddToCart] Button classes:', this.refs.addToCartButton.className);
    } else {
      console.error('[AddToCart] No button found to enable');
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    console.log('[AddToCart] handleClick called', {
      eventType: event.type,
      target: event.target?.tagName,
      button: this.refs.addToCartButton?.tagName,
      buttonDisabled: this.refs.addToCartButton?.disabled,
      buttonType: this.refs.addToCartButton?.type
    });

    const form = this.closest('form');
    console.log('[AddToCart] Form found:', !!form, 'Valid:', form?.checkValidity());

    if (!form?.checkValidity()) {
      // Show validation errors if form is invalid
      console.warn('[AddToCart] Form is invalid, showing errors');
      form?.reportValidity();
      return;
    }

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    console.log('[AddToCart] ProductFormComponent found:', !!productForm);

    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      console.log('[AddToCart] Quantity validation:', validation);
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        console.warn('[AddToCart] Cannot add to cart - max quantity exceeded');
        return;
      }
    }

    const isPuppet = this.refs.addToCartButton.dataset.puppet === 'true';
    console.log('[AddToCart] Is puppet click:', isPuppet);

    if (!isPuppet) {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      console.log('[AddToCart] Animation enabled:', animationEnabled);
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }

    // Explicitly trigger form submission for mobile/iOS compatibility
    // This ensures the submit event is properly fired on all devices,
    // especially iOS where the custom event handling may interfere with
    // the default submit button behavior
    console.log('[AddToCart] About to submit form', {
      formExists: !!form,
      buttonType: this.refs.addToCartButton?.type,
      defaultPrevented: event.defaultPrevented
    });

    if (form && this.refs.addToCartButton.type === 'submit' && !event.defaultPrevented) {
      console.log('[AddToCart] Triggering form submission...');

      // Use requestSubmit() to trigger the submit event properly
      // This method is supported in all modern browsers including iOS Safari 15.4+
      // Use setTimeout to ensure the animation starts before submission
      setTimeout(() => {
        console.log('[AddToCart] Executing submit now');
        try {
          if (typeof form.requestSubmit === 'function') {
            console.log('[AddToCart] Using requestSubmit()');
            form.requestSubmit();
          } else {
            console.log('[AddToCart] Using fallback submit method');
            // Fallback for older browsers - dispatch submit event and then submit
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
            const canSubmit = form.dispatchEvent(submitEvent);
            console.log('[AddToCart] Submit event dispatched, canSubmit:', canSubmit);
            if (canSubmit) {
              form.submit();
            }
          }
        } catch (error) {
          console.error('[AddToCart] Error submitting form:', error);
        }
      }, 50);
    } else {
      console.error('[AddToCart] Cannot submit form - conditions not met', {
        formExists: !!form,
        buttonType: this.refs.addToCartButton?.type,
        defaultPrevented: event.defaultPrevented
      });
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {((event: Event) => void) | undefined} */
  #formSubmitHandler;

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    console.log('[ProductForm] Component connected, setting up form backup listener');

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Set up backup submit listener for mobile/iOS compatibility
    // This ensures the form submission happens even if event delegation fails
    setTimeout(() => {
      const form = this.querySelector('form');
      if (form && !this.#formSubmitHandler) {
        console.log('[ProductForm] Setting up backup submit listener on form');
        this.#formSubmitHandler = (event) => {
          console.log('[ProductForm] Backup submit listener triggered');
          // Call the handleSubmit method
          this.handleSubmit(event);
        };
        form.addEventListener('submit', this.#formSubmitHandler);
      }

      // Debug: Check button state on mobile after connection
      const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
      console.log('[ProductForm] Initial button state check:', {
        buttonExists: !!addToCartButton,
        buttonDisabled: addToCartButton?.disabled,
        variantId: this.refs.variantId?.value,
        productId: this.dataset.productId
      });
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove backup submit listener
    if (this.#formSubmitHandler) {
      const form = this.querySelector('form');
      if (form) {
        form.removeEventListener('submit', this.#formSubmitHandler);
      }
    }

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  /**
   * Handles the click event on the add to cart button.
   * This is used to report validation errors if the form is invalid.
   * @param {PointerEvent} event - The click event.
   */
  handleClick(event) {
    const form = this.querySelector('form');
    if (form && !form.checkValidity()) {
      form.reportValidity();
    }
  }

  handleSubmit(event) {
    console.log('[ProductForm] handleSubmit called', {
      eventType: event.type,
      target: event.target?.tagName,
      productId: this.dataset.productId,
      currentTarget: event.currentTarget?.tagName
    });

    // Prevent duplicate submissions
    if (this.#isSubmitting) {
      console.warn('[ProductForm] Form already submitting, ignoring duplicate submission');
      event.preventDefault();
      return;
    }

    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Set the submitting flag to prevent duplicate submissions
    this.#isSubmitting = true;
    console.log('[ProductForm] Set submitting flag to true');

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    console.log('[ProductForm] Add to cart containers found:', allAddToCartContainers.length);

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) {
      console.warn('[ProductForm] Button disabled, returning early');
      return;
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    console.log('[ProductForm] Form element found:', !!form);

    if (!form) {
      console.error('[ProductForm] Product form element missing');
      throw new Error('Product form element missing');
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    console.log('[ProductForm] FormData prepared', {
      variantId: formData.get('id'),
      quantity: formData.get('quantity')
    });

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    console.log('[ProductForm] Fetching cart add URL:', Theme.routes.cart_add_url);

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => {
        console.log('[ProductForm] Fetch response status:', response.status);
        return response.json();
      })
      .then(async (response) => {
        console.log('[ProductForm] Fetch response:', response);
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error('[ProductForm] Fetch error:', error);
      })
      .finally(() => {
        console.log('[ProductForm] Fetch complete');
        cartPerformance.measureFromEvent('add:user-action', event);
        // Reset the submitting flag to allow new submissions
        this.#isSubmitting = false;
        console.log('[ProductForm] Reset submitting flag to false');
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    console.log('[ProductForm] #onVariantUpdate called', {
      productId: event.detail.data.productId,
      variantResource: event.detail.resource,
      available: event.detail.resource?.available,
      newProduct: event.detail.data.newProduct
    });

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      console.log('[ProductForm] Product ID mismatch, returning');
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    console.log('[ProductForm] Button elements', {
      containerExists: !!currentAddToCartButtonContainer,
      buttonExists: !!currentAddToCartButton,
      buttonDisabled: currentAddToCartButton?.disabled
    });

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
      console.error('[ProductForm] Required button elements not found');
      return;
    }

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      console.log('[ProductForm] Disabling button - variant unavailable');
      currentAddToCartButtonContainer.disable();
    } else {
      console.log('[ProductForm] Enabling button - variant available');
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
